import { useEffect, useRef, useState } from 'react';
import type { BreakdownRow, ClaimCandidate, PostingItem } from '../types';
import { amountAria, money, signedMoney } from '../utils';
import { Check, Spinner, SourceRow } from './common';
import { Link } from 'react-router-dom';
import { approvePostAgain, canPost, getConfirmedSync, postItem, recordLearning } from '../store';
import { getNetworkStatus } from '@/features/networks/store';
import { hasMoneyPermission } from '@/features/shared/permissions';
import { TRAINING } from '../fixtures';
import { cardFeeLine, dentistShort, isOon, itemBreakdown, referenceLine } from '../reference';
import { creditNotice, isCaliforniaScenario } from '../creditMock';
import { PMS } from '@/features/shared/pms';

interface Props {
  item: PostingItem;
  onAnswer: (choice: string) => void;
  onPickClaim: (candidateId: string) => void;
  onSearch: (q: string) => ClaimCandidate[];
  onSomethingWrong: (note: string) => void;
  onReturnChoice: (choice: string) => void;
  onConfirmReturn: () => void;
  onPostMyself: () => void;
  onChangeAnswer: () => void;
  onSeeSteps: (postedId: string) => void;
  onBack?: () => void;
}

function Breakdown({ rows }: { rows: BreakdownRow[] }) {
  return (
    <table className="po-break">
      <tbody>
        {rows.map((r, i) => (
          <tr key={`${r.label}-${String(i)}`} className={r.kind === 'total' ? 'total' : r.soft ? 'soft' : undefined}>
            <td>
              {r.label}
              {r.detail ? <span className="bk-note"> · {r.detail}</span> : null}
              {r.badge ? <span className={`po-badge ${r.badge.tone === 'info' ? 'b-info' : 'b-mute'}`} style={{ marginLeft: 8 }}>{r.badge.label}</span> : null}
            </td>
            <td className={`amt${r.kind === 'neg' ? ' neg' : ''}`} aria-label={amountAria(r.amount)}>
              {r.kind === 'neg' ? signedMoney(r.amount) : r.amount === 0 ? money(0) : money(r.amount)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function kindBadge(item: PostingItem): { label: string; cls: string } | null {
  if (item.kind === 'paid_to_patient') return { label: 'Paid to the patient', cls: 'b-mute' };
  if (item.kind === 'patient_credit') return { label: 'Patient credit', cls: 'b-info' };
  return null;
}

/** Under every breakdown: what ELVA writes to the PMS so the practice can search it later (D199). */
function ReferenceFoot({ item }: { item: PostingItem }) {
  const fee = cardFeeLine(item.payment, item.amount);
  return (
    <div className="po-reffoot">
      {fee ? <p className="po-meta" style={{ margin: '0 0 4px' }}>{fee}</p> : null}
      <p className="po-meta" style={{ margin: 0 }}>
        {referenceLine(item.payment, item.payer.short, item.trace)}
        {item.codes ? ` · ${item.codes}` : ''}
      </p>
    </div>
  );
}

/** The sentences that go with the breakdown of the two new cases (D197, D198). */
function CaseNotes({ item, rows }: { item: PostingItem; rows: BreakdownRow[] }) {
  if (item.kind === 'paid_to_patient' && item.paidToPatient) {
    const last = rows[rows.length - 1];
    const owed = last ? last.amount : 0;
    return (
      <>
        <div className="po-note">{item.payer.short} sent its {money(item.paidToPatient.amount)} to {item.firstName}. It isn’t money you receive.</div>
        <p className="po-case-para">
          ELVA posts a {money(item.amount)} {item.payer.short} payment with the note “{item.payer.short} paid the patient {money(item.paidToPatient.amount)} · {item.paidToPatient.eobLabel}”, closes the claim, and leaves {money(owed)} on {item.firstName}’s balance.
        </p>
      </>
    );
  }
  if (item.kind === 'patient_credit' && item.credit) {
    return <div className="po-note">{creditNotice(item.firstName, item.credit.pronoun, item.credit.sinceIso, isCaliforniaScenario())}</div>;
  }
  return null;
}

function Compare({ item }: { item: PostingItem }) {
  if (!item.compare) return null;
  const hasClaim = item.compare.some((r) => r.claim !== '—');
  return (
    <div className="po-tbl-wrap">
      <table className="po-tbl">
        <thead>
          <tr>
            <th />
            <th>On the ERA</th>
            {hasClaim ? <th>Claim in {PMS}</th> : null}
            {hasClaim ? <th>Match</th> : null}
          </tr>
        </thead>
        <tbody>
          {item.compare.map((r) => (
            <tr key={r.label}>
              <td style={{ color: 'var(--mfg)' }}>{r.label}</td>
              <td>{r.era}</td>
              {hasClaim ? <td>{r.claim}</td> : null}
              {hasClaim ? <td className={r.ok ? 'po-verdict-ok' : 'po-verdict-warn'}>{r.verdict}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SearchCard({ item, onSearch, onPick }: { item: PostingItem; onSearch: (q: string) => ClaimCandidate[]; onPick: (id: string) => void }) {
  const [q, setQ] = useState('');
  const results = onSearch(q);
  return (
    <div style={{ marginTop: 12 }}>
      <label className="po-label" htmlFor="po-claim-search">Which claim is this?</label>
      <input id="po-claim-search" className="po-field" placeholder={`Search ${item.payer.short} open claims`} value={q} onChange={(e) => { setQ(e.target.value); }} />
      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {results.map((c) => (
          <button key={c.id} type="button" className="po-option" onClick={() => { onPick(c.id); }}>
            <span style={{ fontWeight: 600 }}>{c.patient}</span>
            <span className="po-meta">· {c.serviceDate.replace(', 2026', '')} · {c.procedure} · {money(c.billed)} · claim {c.claimNumber}</span>
          </button>
        ))}
        {results.length === 0 ? <p className="po-meta">No open claims match.</p> : null}
      </div>
    </div>
  );
}

/**
 * D269: after "It isn't there" ELVA checked once more and still can't confirm. Posting again could post it
 * twice, so only a person with the override permission can do it, with a one-line reason.
 */
function PostAgainCard({ item, onPostAgain }: { item: PostingItem; onPostAgain: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  const allowed = hasMoneyPermission();
  return (
    <section className="po-warn-card" aria-labelledby="po-again-title">
      <strong id="po-again-title">ELVA still can’t confirm whether it’s in {PMS}. Posting again could post it twice.</strong>
      <p className="po-meta" style={{ margin: '6px 0 0' }}>You said it isn’t there. That’s recorded as what you saw, not as proof.</p>
      {item.override ? (
        <p style={{ margin: '10px 0 0' }}>Approved to post again: “{item.override.reason}”</p>
      ) : allowed ? (
        <div style={{ marginTop: 12 }}>
          <label className="po-label" htmlFor="po-again-reason">Why is it safe to post it again?</label>
          <input id="po-again-reason" className="po-field" placeholder="One line, for the record" value={reason} onChange={(e) => { setReason(e.target.value); }} />
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" className="po-btn po-btn-sm" disabled={!reason.trim()} aria-describedby={reason.trim() ? undefined : 'po-again-reason-hint'} onClick={() => { onPostAgain(reason.trim()); }}>Post it again anyway</button>
            {reason.trim() ? null : <span id="po-again-reason-hint" className="po-disabled-reason">Write the reason first.</span>}
          </div>
        </div>
      ) : (
        <p className="po-meta" style={{ margin: '10px 0 0' }}>An owner or admin can approve posting it again.</p>
      )}
    </section>
  );
}

export function Detail(props: Props) {
  const { item } = props;
  const headingRef = useRef<HTMLHeadingElement>(null);
  const resultRef = useRef<HTMLHeadingElement>(null);
  const [reveal, setReveal] = useState<'search' | 'wrong' | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [wrongText, setWrongText] = useState('');
  const [phase, setPhase] = useState<'idle' | 'posting' | 'failed'>('idle');
  const [liveStep, setLiveStep] = useState('');
  const [failMsg, setFailMsg] = useState('');
  const [learned, setLearned] = useState(false);

  const posted = item.removed === 'posted';

  useEffect(() => {
    headingRef.current?.focus();
    setReveal(null);
    setPicked(null);
    setWrongText('');
    setPhase('idle');
    setLiveStep('');
    setFailMsg('');
    setLearned(false);
  }, [item.id]);

  useEffect(() => {
    if (posted) resultRef.current?.focus();
  }, [posted]);

  const onRadio = (optId: string): void => {
    const opt = item.question?.options.find((o) => o.id === optId);
    if (!opt) return;
    setPicked(optId);
    if (opt.effect === 'search') { setReveal('search'); return; }
    if (opt.effect === 'something_wrong') { setReveal('wrong'); return; }
    setReveal(null);
    props.onAnswer(optId);
  };

  const runPost = async (): Promise<void> => {
    setPhase('posting');
    for await (const ev of postItem(item.id)) {
      if ('step' in ev) setLiveStep(ev.step);
      else if ('failed' in ev) { setFailMsg(ev.failed); setPhase('failed'); return; }
    }
    // success: store set item.removed='posted' and emitted; component re-renders as posted
  };

  const isNotOurs = item.answered?.effect === 'not_our_patient';
  const netStatus = item.networkQ ? getNetworkStatus(item.networkQ.dentist, item.networkQ.payer) : null;
  // D238/D269: while ELVA checks or a person must approve, the normal Post button is not offered.
  const pmsHeld = Boolean(item.pmsCheck) && !item.override;
  const elvaBusy = item.group === 'checking' || item.pmsCheck?.stage === 'rechecking';

  // ---- Posted success view ----
  if (posted) {
    return (
      <main className="po-detail">
        {props.onBack ? <button type="button" className="po-back" onClick={props.onBack}>← All payments</button> : null}
        <div className="po-detail-inner">
          <WhoHeader item={item} headingRef={headingRef} />
          <section className="po-success">
            <h3 className="po-success-title" tabIndex={-1} ref={resultRef}>Posted in {PMS}</h3>
            <p style={{ margin: '0 0 10px' }}>ELVA posted {signedMoney(item.amount)} on {item.firstName}’s claim at 10:42 AM.</p>
            {item.postedTicks.map((t, i) => (
              <div key={`pt-${String(i)}`} className="po-tickrow"><span className="po-tick" aria-hidden="true"><Check /></span><div>{t.text}</div></div>
            ))}
            <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
              <button type="button" className="po-btn po-btn-sm" onClick={() => { props.onSeeSteps(`POSTED-${item.id}`); }}>See every step</button>
              {hasMoneyPermission() ? <button type="button" className="po-btn po-btn-ghost po-btn-sm" onClick={() => { props.onSeeSteps(`POSTED-${item.id}`); }}>Undo this posting</button> : null}
            </div>
            <p className="po-meta" style={{ marginTop: 10 }}>{referenceLine(item.payment, item.payer.short, item.trace)}</p>
            <p className="po-meta" style={{ marginTop: 4 }}>Confirmed by Ava Thompson (Lone Star Billing) · posted by ELVA’s agent in {PMS} · {getConfirmedSync()} of {TRAINING.milestone} confirmed</p>
          </section>
          {item.learning && !learned ? (
            <section className="po-learning">
              <div className="po-handled-badge"><Check /> ELVA learned something</div>
              <p style={{ margin: '0 0 12px' }}>{item.learning.text}</p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" className="po-btn po-btn-sm po-btn-primary" onClick={() => { recordLearning(item.id, 'right'); setLearned(true); }}>That’s right</button>
                <button type="button" className="po-btn po-btn-sm" onClick={() => { recordLearning(item.id, 'one-off'); setLearned(true); }}>This was a one-off</button>
              </div>
              <p className="po-meta" style={{ marginTop: 10 }}>{item.learning.scope}</p>
            </section>
          ) : null}
          <p className="po-history">History: {item.history.join(' · ')}</p>
        </div>
      </main>
    );
  }

  const disabledReason = isNotOurs
    ? (item.returnChoice ? null : 'Choose how to return the money first.')
    : !canPost(item) ? 'Choose an answer first.' : null;
  const badge = kindBadge(item);
  const rows = itemBreakdown(item);

  return (
    <main className="po-detail">
      {props.onBack ? <button type="button" className="po-back" onClick={props.onBack}>← All payments</button> : null}
      <div className="po-detail-inner">
        <WhoHeader item={item} headingRef={headingRef} />

        {/* what happened / ready */}
        <section className="po-card">
          {badge ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start', marginBottom: item.whatHappened.body ? 6 : 0 }}>
              <span className={`po-badge ${badge.cls}`}>{badge.label}</span>
              <h3 className="po-h3" style={{ fontSize: 19 }}>{item.whatHappened.headline}</h3>
            </div>
          ) : (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: item.whatHappened.body ? 6 : 0 }}>
            <span className={`po-badge ${item.group === 'ready' ? 'b-ok' : 'b-warn'}`}>{item.group === 'needs_you' ? 'Needs your decision' : item.group === 'checking' ? `Checking with ${PMS}` : 'Ready'}</span>
            <h3 className="po-h3">{item.whatHappened.headline}</h3>
          </div>
          )}
          {item.whatHappened.body ? <p className="po-consequence">{item.whatHappened.body}</p> : null}
          {item.ticks ? item.ticks.map((t, i) => (
            <div key={`tk-${String(i)}`} className="po-tickrow"><span className="po-tick" aria-hidden="true"><Check /></span><div>{t.text}</div></div>
          )) : null}
        </section>

        {/* evidence */}
        {item.compare ? <section className="po-card"><h3 className="po-h3" style={{ marginBottom: 12 }}>The payment and the claim</h3><Compare item={item} /></section> : null}
        {item.breakdown && !isNotOurs ? (
          <section className="po-card">
            <h3 className="po-h3" style={{ marginBottom: 6 }}>{badge && item.claimOf ? `What ELVA will post on the ${item.claimOf} claim` : item.group === 'needs_you' ? 'What ELVA will post' : 'What gets posted'}</h3>
            {item.procedureLine ? <p className="po-meta" style={{ margin: '0 0 4px' }}>{item.procedureLine}</p> : null}
            <Breakdown rows={rows} />
            <CaseNotes item={item} rows={rows} />
            <ReferenceFoot item={item} />
          </section>
        ) : null}

        {item.check ? (
          <section className="po-timing" style={item.check.ok ? undefined : { background: 'var(--wsoft)' }}>
            <span className="po-tick" aria-hidden="true" style={item.check.ok ? undefined : { background: 'var(--card)', color: 'var(--warning)' }}>{item.check.ok ? <Check /> : '!'}</span>
            <div>{item.check.text}</div>
          </section>
        ) : null}

        {/* not-our-patient: return-money choices replace the breakdown */}
        {isNotOurs ? (
          <section className="po-card">
            <p style={{ margin: '0 0 12px' }}>{item.notOurPatientNote}</p>
            <div className="po-options" role="radiogroup" aria-label="Return the money">
              {[{ id: 'send_back', label: 'Send it back' }, { id: 'let_take_back', label: 'Let Delta take it back' }].map((o) => (
                <button key={o.id} type="button" role="radio" aria-checked={item.returnChoice === o.id} className="po-option" style={item.returnChoice === o.id ? { background: 'var(--psoft)', borderColor: 'var(--primary)', fontWeight: 600 } : undefined} onClick={() => { props.onReturnChoice(o.id); }}>{o.label}</button>
              ))}
            </div>
            <div style={{ marginTop: 6 }}>
              <button type="button" className="po-btn po-btn-ghost po-btn-sm" onClick={props.onChangeAnswer}>Change answer</button>
            </div>
          </section>
        ) : item.pmsCheck?.stage === 'checking' ? (
          <section className="po-card">
            <p style={{ margin: 0 }}>ELVA has checked {PMS} {item.pmsCheck.checks} of 3 times. It checks again by itself. If there’s still no answer after 3 checks, it asks you whether the payment is there.</p>
            <p className="po-meta" style={{ margin: '8px 0 0' }}>Nothing for you to do.</p>
          </section>
        ) : item.pmsCheck?.stage === 'rechecking' ? (
          <section className="po-card">
            <div className="po-live" aria-live="polite"><Spinner /><span>ELVA is checking {PMS} once more…</span></div>
            <p className="po-meta" style={{ margin: '8px 0 0' }}>You said it isn’t there. That’s recorded as what you saw, not as proof. Nothing is posted meanwhile.</p>
          </section>
        ) : item.pmsCheck?.stage === 'unconfirmed' ? (
          <PostAgainCard item={item} onPostAgain={(reason) => { if (approvePostAgain(item.id, reason)) void runPost(); }} />
        ) : item.networkQ && netStatus !== 'unconfirmed' ? (
          <div className="po-answer">
            <span className="po-badge b-ok"><Check /></span>
            <span>{item.networkQ.dentist} is {netStatus === 'out' ? 'out of network' : 'in network'} with {item.networkQ.payer}. Logged in <Link to="/settings/practice#networks" className="po-src" style={{ textDecoration: 'underline' }}>Settings › Practice details › Networks</Link>.</span>
          </div>
        ) : item.question && item.group === 'needs_you' && !item.answered ? (
          <section className="po-card" aria-labelledby="po-q-title">
            <h3 className="po-h3" id="po-q-title" style={{ marginBottom: 10 }}>{item.question.text}</h3>
            <div className="po-options" role="radiogroup" aria-labelledby="po-q-title">
              {item.question.options.map((o) => (
                <button key={o.id} type="button" role="radio" aria-checked={picked === o.id} className="po-option" style={picked === o.id ? { background: 'var(--psoft)', borderColor: 'var(--primary)', fontWeight: 600 } : undefined} onClick={() => { onRadio(o.id); }}>{o.label}</button>
              ))}
            </div>
            {reveal === 'search' ? <SearchCard item={item} onSearch={props.onSearch} onPick={props.onPickClaim} /> : null}
            {reveal === 'wrong' ? (
              <div style={{ marginTop: 12 }}>
                <input className="po-field" placeholder="Tell ELVA what’s wrong" value={wrongText} onChange={(e) => { setWrongText(e.target.value); }} />
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <button type="button" className="po-btn po-btn-primary po-btn-sm" disabled={!wrongText.trim()} aria-describedby={wrongText.trim() ? undefined : 'po-send-reason'} onClick={() => { props.onSomethingWrong(wrongText.trim()); }}>Send</button>
                  {wrongText.trim() ? null : <span id="po-send-reason" className="po-disabled-reason">Write what’s wrong first.</span>}
                </div>
              </div>
            ) : null}
            {item.question.meta ? <p className="po-meta" style={{ margin: '10px 0 0' }}>{item.question.meta}</p> : null}
          </section>
        ) : item.answered?.effect === 'enable_post' ? (
          <div className="po-answer"><span className="po-badge b-ok"><Check /></span><span>Answer recorded.</span><button type="button" className="po-btn po-btn-ghost po-btn-sm" style={{ marginLeft: 'auto' }} onClick={props.onChangeAnswer}>Change answer</button></div>
        ) : null}

        <SourceRow sources={item.sources} />

        {/* action bar */}
        {phase === 'posting' ? (
          <div className="po-live" aria-live="polite"><Spinner /><span>{liveStep || `Posting in ${PMS}…`}</span></div>
        ) : phase === 'failed' ? (
          <div className="po-warn-card">
            <strong>Posting checked and reversed.</strong>
            <p style={{ margin: '6px 0 12px' }}>{failMsg}</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="po-btn po-btn-primary po-btn-sm" onClick={() => { void runPost(); }}>Try again</button>
              <button type="button" className="po-btn po-btn-sm" onClick={props.onPostMyself}>I’ll post it myself</button>
            </div>
          </div>
        ) : (
          <div className="po-actionbar">
            {isNotOurs ? (
              <button type="button" className="po-btn po-btn-primary po-btn-lg" disabled={!item.returnChoice} aria-describedby={item.returnChoice ? undefined : 'po-disabled'} onClick={props.onConfirmReturn}>Confirm</button>
            ) : pmsHeld ? null : (
              <button type="button" className="po-btn po-btn-primary po-btn-lg" disabled={Boolean(disabledReason)} aria-describedby={disabledReason ? 'po-disabled' : undefined} onClick={() => { void runPost(); }}>Post to {PMS}</button>
            )}
            {!isNotOurs && !elvaBusy ? <button type="button" className="po-btn po-btn-ghost" onClick={props.onPostMyself}>I’ll post it myself</button> : null}
            {disabledReason && !pmsHeld ? <span id="po-disabled" className="po-disabled-reason">{disabledReason}</span> : item.pmsCheck?.stage === 'rechecking' ? <span className="po-actionbar-meta">ELVA is checking. Nothing else happens until it answers.</span> : item.pmsCheck?.stage === 'unconfirmed' ? <span className="po-actionbar-meta">ELVA won’t post this again without approval.</span> : item.underBar ? <span className="po-actionbar-meta">{item.underBar}</span> : <span className="po-actionbar-meta">ELVA posts it in {PMS}, then checks the claim and the balance.</span>}
          </div>
        )}

        <p className="po-history">History: {item.history.join(' · ')}</p>
      </div>
    </main>
  );
}

function WhoHeader({ item, headingRef }: { item: PostingItem; headingRef: React.RefObject<HTMLHeadingElement | null> }) {
  const oon = isOon(item);
  const claim = kindBadge(item) && item.claimOf ? ` · claim of ${item.claimOf}` : '';
  const network = oon ? ` · ${dentistShort(item.dentist)} · out of network with ${item.payer.short}` : '';
  return (
    <section className="po-claimhead" aria-labelledby="po-name">
      <div>
        <h2 id="po-name" className="po-patient" tabIndex={-1} ref={headingRef}>{item.patient}</h2>
        <p className="po-chsub">{item.payer.name} · member {item.memberId}{claim}{network}</p>
        <p className="po-chmeta">{item.headMeta ?? `ERA of ${item.eraDisplay.replace(', 2026', '')} · deposited ${item.depositedDisplay} · trace ${item.trace}`}</p>
      </div>
      <div className="po-right">
        <div className="po-meta">{item.amountLabel}</div>
        <div className="po-bigfig num" aria-label={amountAria(item.amount)}>{signedMoney(item.amount)}</div>
      </div>
    </section>
  );
}
